import api from '@/api';
import useMeQuery from '@/api/queries/me/useMeQuery';
import useUpdateCachedUser from '@/api/queries/useUpdateCachedUser';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import type { Ref } from 'vue';
import type { UserData } from '@/api/queries/user/types/user';
import { useToast } from 'vue-toastification';
import { inject } from 'vue';
import type { Translate } from '@/plugins/i18n/types';

const patchUserData = async (uuid?: string, userData?: UserData) => {
  if (!uuid) throw new AxiosError('Auth UUID is necessary to patch user data');

  const res = await api<UserData>({
    method: 'patch',
    url: `/user/${uuid}/data`,
    data: {
      ...userData,
    },
  });

  return res;
};

export function useUserDataMutation(
  userData: Ref<UserData & { industry_id?: number }>,
  options?: { silent?: boolean },
) {
  const { auth } = useMeQuery();
  const toast = useToast();
  const t = inject('t') as Translate;
  const { silent = false } = options ?? {};
  const queryClient = useQueryClient();

  const { mutateAsync: updateUserData, isPending: isLoading } = useMutation({
    mutationFn: () => patchUserData(auth.value?.uuid, userData.value),
    onSuccess: () => {
      const industry = userData.value.industry_id
        ? { id: userData.value.industry_id }
        : auth.value?.user_data?.industry;

      const data: Partial<UserData> = {
        ...auth.value?.user_data,
        ...userData.value,
        industry,
      };

      useUpdateCachedUser({ user_data: data }, queryClient);
      if (!silent) {
        toast.success(t('general.save_success'));
      }
    },
    onError: () => {
      if (!silent) toast.error(t('general.error.general'));
    },
  });

  return { updateUserData, isLoading };
}
